import React, { useState, useEffect, useRef } from 'react'
import { c, t } from 'ttag'
import { Box, ListItem, ListItemIcon, ListItemText, CircularProgress } from '@material-ui/core'
import YumDialog from '../../../../components/YumDialog/YumDialog'
import YumDialogTitle from '../../../../components/YumDialogTitle/YumDialogTitle'
import YumDialogContent from '../../../../components/YumDialogContent/YumDialogContent'
import YumDialogContentText from '../../../../components/YumDialogContentText/YumDialogContentText'
import YumDialogActions from '../../../../components/YumDialogActions/YumDialogActions'
import YumCancelButton from '../../../../components/YumCancelButton/YumCancelButton'
import YumSubmitButton from '../../../../components/YumSubmitButton/YumSubmitButton'
import YumSwitch from '../../../../components/YumSwitch/YumSwitch'
import YumSelect from '../../../../components/YumSelect/YumSelect'
import { ReactComponent as GroupActiveIcon } from '../../../../assets/v3/icons/group-active.svg'
import { ReactComponent as GroupInactiveIcon } from '../../../../assets/v3/icons/group-inactive.svg'
import { ReactComponent as ChallengeIcon } from '../../../../assets/v3/icons/announcements/challenge.svg'
import {
  CompanyId,
  AssignTeamsRandomly$Request,
  UserDto,
  DivisionRow,
  UserCompanyDto,
  GoalId,
  GetAssignRandomTeamsFilters$Response,
} from '../../../../services/api.types'
import { formatThousands } from '../../../../common/helpers/translations'
import { red } from '../../../../styles/material-ui-theme-v2'
import YumPill from '../../../Announcements/components/CreateAnnouncement/components/YumPill/YumPill'

interface AssignTeamsRandomlyProps {
  open: boolean
  onClose: () => void
  onSubmit: (payload: AssignTeamsRandomly$Request) => void
  companyId: CompanyId
  companyTeams: DivisionDto[]
  companyUsers: UserDto[]
  usersLoading: boolean
  userCompany: UserCompanyDto
  loading: boolean
  filtersData: {
    loading: boolean
    data: GetAssignRandomTeamsFilters$Response | null
  }
  onFiltersChange: (includeUsersWithTeams: boolean, challengeId?: GoalId) => void
}

const AssignTeamsRandomly: React.FC<AssignTeamsRandomlyProps> = ({
  open,
  onClose,
  onSubmit,
  companyId,
  companyTeams,
  companyUsers,
  usersLoading,
  userCompany,
  loading,
  filtersData = { loading: false, data: null },
  onFiltersChange,
}) => {
  const [includeUsersWithTeams, setIncludeUsersWithTeams] = useState(false)
  const [challengeFilter, setChallengeFilter] = useState(false)
  const [selectedChallengeId, setSelectedChallengeId] = useState<GoalId | ''>('')
  const isSubmittingRef = useRef(false)

  // Close dialog when request completes
  useEffect(() => {
    // If we were loading and now we're not, and we had submitted the form, close the dialog
    if (!loading && isSubmittingRef.current) {
      isSubmittingRef.current = false
      onClose()
    }
  }, [loading, onClose])

  // Reset form values when the dialog closes
  useEffect(() => {
    if (!open) {
      // Reset form state when dialog is closed
      resetForm()
    }
  }, [open])

  // Reset form values when the dialog closes
  useEffect(() => {
    // Pick the first challenge by default
    if (challengeFilter && selectedChallengeId === '' && challenges && challenges.length > 0) {
      setSelectedChallengeId(challenges[0].id)
      onFiltersChange(includeUsersWithTeams, challenges[0].id)
    }
  }, [challengeFilter])

  // Function to reset form values to initial state
  const resetForm = () => {
    setIncludeUsersWithTeams(false)
    setChallengeFilter(false)
    setSelectedChallengeId('')
    isSubmittingRef.current = false

    // Also reset the filter at parent level
    onFiltersChange(false, undefined)
  }

  const handleIncludeUsersWithTeamsChange = () => {
    const newValue = !includeUsersWithTeams
    setIncludeUsersWithTeams(newValue)
    onFiltersChange(newValue, challengeFilter && selectedChallengeId ? selectedChallengeId : undefined)
  }

  const handleChallengeFilterChange = () => {
    const newValue = !challengeFilter
    setChallengeFilter(newValue)
    onFiltersChange(includeUsersWithTeams, newValue && selectedChallengeId ? selectedChallengeId : undefined)
  }

  const handleChallengeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newValue = event.target.value as GoalId | ''
    setSelectedChallengeId(newValue)
    onFiltersChange(includeUsersWithTeams, challengeFilter && newValue ? newValue : undefined)
  }

  // Get user count from the backend response
  const getUserCount = () => {
    return filtersData?.data?.userCount || 0
  }

  // Check if there are enough team slots available based on backend response
  const checkEnoughTeamSlots = () => {
    if (!filtersData?.data) return true
    return !filtersData.data.notEnoughTeams
  }

  // Get warning message if not enough slots
  const getWarningMessage = () => {
    if (!checkEnoughTeamSlots() && filtersData?.data) {
      const { availableTeamSlots, availableTeams, userCount } = filtersData.data
      // TODO:
      const x = availableTeams == 0 ? 2 : Math.ceil((userCount - availableTeamSlots) / userCompany?.maxTeamSize)
      return (
        <YumDialogContentText style={{ color: red, marginTop: '10px', fontWeight: 'bold', textAlign: 'center' }}>
          {t`Warning: Not enough available team slots! Please create at least ${x} teams first.`}
        </YumDialogContentText>
      )
    }
    return null
  }

  // Get text for affected users
  const getAffectedUsersText = () => {
    const userCount = getUserCount()
    const availableSlots = filtersData?.data?.availableTeamSlots || 0
    const availableTeams = filtersData?.data?.availableTeams || 0
    const maxTeamSize = userCompany?.maxTeamSize || 0

    if (filtersData.loading || usersLoading) {
      return <>{t`Loading...`}</>
    }

    if (userCount === 0) {
      return (
        <div>
          {t`Users to assign`}: <YumPill text={'0'} danger={true} />
        </div>
      )
    }

    return (
      <div>
        <div>
          {t`Users to assign`}: <YumPill text={formatThousands(userCount)} />
        </div>
        <div style={{ marginTop: '10px' }}>
          {`${t`Teams`} (${c('Adjective').t`Open`})`}:{' '}
          <YumPill text={formatThousands(availableTeams)} danger={availableTeams === 0} />
        </div>
        {maxTeamSize > 0 ? (
          <>
            <div style={{ marginTop: '10px' }}>
              {t`Max team size`}: <YumPill text={formatThousands(maxTeamSize)} />
            </div>
            <div style={{ marginTop: '10px' }}>
              {t`Available slots`}: <YumPill text={formatThousands(availableSlots)} />
            </div>
          </>
        ) : null}
      </div>
    )
  }

  // Handle submit
  const handleSubmit = () => {
    if (!checkEnoughTeamSlots()) {
      return
    }

    // Mark that we've submitted the form
    isSubmittingRef.current = true

    onSubmit({
      companyId,
      includeUsersWithTeams,
      goalId: challengeFilter && selectedChallengeId ? selectedChallengeId : undefined,
    })
  }

  // Wrap the onClose with our resetForm function
  const handleClose = () => {
    onClose()
  }

  const isFiltersLoading = filtersData?.loading || false
  const challenges = filtersData?.data?.challenges || []

  return (
    <YumDialog open={open}>
      <YumDialogTitle handleClose={handleClose}>{t`Assign random teams`}</YumDialogTitle>
      <YumDialogContent>
        {/* <YumDialogContentText style={{ marginBottom: '16px' }}>
          {t`Are you sure you want to assign users to teams randomly?`}
        </YumDialogContentText> */}
        <ListItem
          role={undefined}
          dense
          button
          onClick={handleIncludeUsersWithTeamsChange}
          style={{ marginBottom: 10 }}
          disabled={loading || isFiltersLoading}
        >
          <ListItemIcon>
            <GroupActiveIcon />
          </ListItemIcon>
          <ListItemText
            primary={t`Include users with teams`}
            secondary={t`Excludes team creators and users from locked teams.`}
            secondaryTypographyProps={{
              style: { fontSize: '12px', lineHeight: '14px', marginTop: '4px' },
            }}
          />
          <ListItemIcon>
            <YumSwitch
              checked={includeUsersWithTeams}
              onChange={handleIncludeUsersWithTeamsChange}
              disabled={loading || isFiltersLoading}
            />
          </ListItemIcon>
        </ListItem>
        {challenges && challenges.length > 0 ? (
          <ListItem
            role={undefined}
            dense
            button
            onClick={handleChallengeFilterChange}
            style={{ marginBottom: 10 }}
            disabled={loading || isFiltersLoading}
          >
            <ListItemIcon>
              <ChallengeIcon />
            </ListItemIcon>
            <ListItemText
              primary={t`Only challenge participants`}
              secondaryTypographyProps={{
                style: { fontSize: 12 },
              }}
            />
            <ListItemIcon>
              <YumSwitch
                checked={challengeFilter}
                onChange={handleChallengeFilterChange}
                disabled={loading || isFiltersLoading}
              />
            </ListItemIcon>
          </ListItem>
        ) : null}
        {challengeFilter && (
          <ListItem role={undefined} dense style={{ padding: 0 }} disabled={loading || isFiltersLoading}>
            <YumSelect
              fullWidth
              //startIcon={ChallengeIcon}
              label={t`Challenge`}
              onChange={handleChallengeChange}
              value={selectedChallengeId}
              disabled={loading || isFiltersLoading || challenges.length === 0}
            >
              {/*<option value=""></option>*/}
              {challenges.map((challenge) => (
                <option key={challenge.id} value={challenge.id}>
                  {challenge.title} ({formatThousands(challenge.count)})
                </option>
              ))}
            </YumSelect>
          </ListItem>
        )}

        <YumDialogContentText>
          <div
            style={{
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {isFiltersLoading ? <CircularProgress size={16} style={{ marginRight: 8 }} /> : null}
            {getAffectedUsersText()}
          </div>
        </YumDialogContentText>
        {getWarningMessage()}
      </YumDialogContent>
      <YumDialogActions>
        <YumCancelButton onClick={handleClose} disabled={loading} />
        <YumSubmitButton
          text={t`Assign`}
          onClick={handleSubmit}
          disabled={!checkEnoughTeamSlots() || loading || isFiltersLoading || getUserCount() === 0}
          submitInProgress={loading}
          immersiveLoader={true}
        />
      </YumDialogActions>
    </YumDialog>
  )
}

export default AssignTeamsRandomly
